import { parseInstalledPowerInput, INSTALLED_POWER_ERROR } from "../../../../shared/installedPower";
import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Info, XCircle, Plus, Trash2, CheckCircle2, Calendar, Wrench, User } from "lucide-react";
import { toast } from "sonner";
import { getContractErrorMessage } from "@/lib/contractErrorMessages";
import { resolveInstallationTitle, INSTALLATION_SHORT_NAME_MAX_LENGTH } from "@/lib/installationContext";
import { LegacyEconomicCorrectionDialog } from "./LegacyEconomicCorrectionDialog";

const WORK_ORDER_LABELS: Record<string, string> = { installation: "Instalación", breakdown: "Avería", maintenance: "Mantenimiento" };

const INSTALLATION_ERROR_MAP: Record<string, string> = {
  FORBIDDEN: "No tienes permiso para realizar esta operación.",
  CONTRACT_NOT_FOUND: "El contrato no existe.",
  CONTRACT_ARCHIVED: "El contrato está archivado.",
  CONTRACT_COMPLETED: "El contrato está completado.",
  CONTRACT_CANCELLED: "El contrato está cancelado.",
  CONTRACT_INACTIVE: "El contrato no está activo para vincular instalaciones.",
  CUSTOMER_NOT_FOUND_OR_NOT_ACTIVE: "El cliente del contrato no está activo.",
  CUSTOMER_INSTALLATION_SITE_REQUIRED: "Selecciona o crea una sede para la instalación.",
  CUSTOMER_SITE_NOT_FOUND: "La sede seleccionada no pertenece al cliente.",
  INSTALLATION_COUNTER_NOT_INITIALIZED: "El contador de expedientes no está inicializado.",
  PM_NOT_FOUND: "El Project Manager seleccionado no existe.",
  PM_NOT_ELIGIBLE: "El usuario seleccionado no puede ser Project Manager.",
  INVALID_CONTACT_OWNERSHIP: "El contacto seleccionado no pertenece al cliente.",
  PLANNING_SEGMENT_TITLE_REQUIRED: "El título de la fase de planificación es obligatorio.",
  PLANNING_SEGMENT_INVALID_DATES: "La fecha de fin debe ser posterior o igual a la fecha de inicio.",
  INSTALLATION_NOT_FOUND: "La instalación seleccionada no existe.",
  INSTALLATION_ALREADY_IN_CONTRACT: "La instalación ya está vinculada a otro contrato.",
  INSTALLATION_ALREADY_LINKED: "La instalación ya está vinculada a otro contrato.",
  INSTALLATION_WRONG_CUSTOMER: "La instalación pertenece a otro cliente y no se puede vincular.",
  INSTALLATION_CUSTOMER_REQUIRED: "La instalación debe tener un cliente asignado.",
  CUSTOMER_RESOLUTION_REQUIRED: "Debes confirmar la asignación del cliente antes de vincular esta instalación legacy.",
  INSTALLATION_REQUIRES_RECONCILIATION: "Esta instalación tiene datos económicos legacy que deben reconciliarse antes de poder vincularla.",
  ECONOMIC_RECONCILIATION_REQUIRED: "Debes confirmar la reconciliación económica antes de vincular esta instalación.",
  LINK_PREVIEW_QUERY_FAILED: "No se pudo verificar la instalación en el servidor; la vinculación queda bloqueada.",
  AUDIT_WRITE_FAILED: "No se pudo guardar la auditoría; no se ha aplicado ningún cambio.",
  DATA_INTEGRITY_BLOCKED: "La instalación contiene datos incompatibles y no se puede vincular.",
  ECONOMIC_RECONCILIATION_ROW_COUNT_MISMATCH: "La reconciliación no afectó exactamente a las filas previstas; no se ha aplicado ningún cambio.",
  "CONTRACT_CAPABILITY_DENIED:WRITE_ECONOMIC": "No tienes permiso (WRITE_ECONOMIC) para reconciliar la economía de la instalación.",
  "CONTRACT_CAPABILITY_DENIED:MANAGE_PARTIES": "No tienes permiso (MANAGE_PARTIES) para resolver la identidad del cliente.",
  BLOCKED_STRATEGY_CONFLICT: "La estrategia de facturación del contrato no es compatible con esta vinculación.",
  BLOCKED_CEILING_CONFLICT: "El importe supera el techo disponible del contrato.",
  BLOCKED_IDENTITY_CONFLICT: "Conflicto de identidad de cliente: la instalación no puede vincularse a este contrato.",
  NEEDS_SITE_RESOLUTION: "La instalación no tiene sede asignada y debe resolverse antes de vincularla.",
  INSTALLATION_SITE_CONFLICT: "La instalación ya tiene una sede distinta y no se puede sustituir durante la vinculación.",
  INSTALLATION_SITE_SELECTION_AMBIGUOUS: "Selecciona una sede existente o crea una nueva, no ambas.",
  SITE_NOT_FOUND: "La sede seleccionada no existe o está inactiva.",
  SITE_OTHER_CUSTOMER: "La sede seleccionada está asociada a otro cliente.",
  SITE_DATA_REQUIRED: "Indica el nombre y la dirección de la nueva sede.",
  LEGACY_CUSTOMER_RESOLUTION_REQUIRES_MANAGE_PARTIES: "No tienes permiso para resolver la identidad de una instalación legacy.",
};

const CLASSIFICATION_LABEL: Record<string, string> = {
  SAFE_TO_LINK: "Listo para vincular",
  SAFE_LINK: "Listo para vincular",
  NEEDS_CUSTOMER_RESOLUTION: "Requiere asignación de cliente",
  NEEDS_SITE_RESOLUTION: "Requiere resolución de sede",
  NEEDS_ECONOMIC_RECONCILIATION: "Requiere reconciliación económica",
  CONFLICT_OTHER_CUSTOMER: "Conflicto: otro cliente",
  CONFLICT_OTHER_CONTRACT: "Conflicto: otro contrato",
  DATA_INTEGRITY_BLOCKED: "Bloqueado: integridad de datos",
  BLOCKED_STRATEGY_CONFLICT: "Bloqueado: conflicto de estrategia",
  BLOCKED_CEILING_CONFLICT: "Bloqueado: techo económico",
  BLOCKED_IDENTITY_CONFLICT: "Bloqueado: conflicto de identidad",
};

const ECONOMIC_STATUS_LABEL: Record<string, string> = {
  draft: "Borrador",
  pending: "Pendiente",
  pending_pm_review: "Pendiente de revisión",
  pm_approved: "Aprobada por PM",
  sent_to_client: "Enviado al cliente",
  client_accepted: "Aceptada por cliente",
  approved: "Aprobado",
  collected: "Cobrado",
  ready_to_invoice: "Lista para facturar",
  accounting_notified: "Contabilidad notificada",
  invoiced: "Facturado",
  pending_collection: "Pendiente de cobro",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

const INSTALLATION_STATUS_LABEL: Record<string, string> = {
  pending: "Pendiente",
  active: "Activa",
  in_progress: "En ejecución",
  completed: "Completada",
  cancelled: "Cancelada",
  archived: "Archivada",
};

const GENERAL_CONTACT_VALUE = "__customer_general__";

type InstallationContactChoice = {
  id: number;
  assignmentId: number;
  displayName: string;
  primaryEmail: string | null;
  primaryPhone: string | null;
  role: string;
  siteId?: number | null;
  customerContractId?: number | null;
  installationId?: number | null;
  scope: "customer" | "site" | "contract" | "installation";
};

function installationContactChoicePriority(
  contact: InstallationContactChoice,
  context: { contractId: number; siteId: number | null },
): number {
  if (contact.scope === "site" && context.siteId != null && Number(contact.siteId) === context.siteId) return 0;
  if (contact.scope === "contract" && Number(contact.customerContractId) === context.contractId) return 1;
  return 2;
}

function dedupeInstallationContactChoices(
  contacts: InstallationContactChoice[],
  context: { contractId: number; siteId: number | null },
): InstallationContactChoice[] {
  return contacts
    .map((contact, index) => ({ contact, index }))
    .filter(({ contact }) => Number.isSafeInteger(contact.id) && contact.id > 0 && Number.isSafeInteger(contact.assignmentId) && contact.assignmentId > 0)
    .filter(({ contact }) => contact.scope === "customer"
      || (contact.scope === "contract" && Number(contact.customerContractId) === context.contractId)
      || (contact.scope === "site" && context.siteId != null && Number(contact.siteId) === context.siteId))
    .sort((a, b) => {
      const priority = installationContactChoicePriority(a.contact, context) - installationContactChoicePriority(b.contact, context);
      if (priority !== 0) return priority;
      const assignmentOrder = a.contact.assignmentId - b.contact.assignmentId;
      return assignmentOrder || a.index - b.index;
    })
    .reduce<InstallationContactChoice[]>((choices, { contact }) => {
      if (!choices.some((choice) => choice.id === contact.id)) choices.push(contact);
      return choices;
    }, []);
}

const NOTICE_EVENT_LABEL: Record<string, string> = {
  accounting_notification: "Aviso a contabilidad",
  invoice_due: "Factura pendiente",
  payment_due: "Pago pendiente",
  cancellation: "Cancelación",
};

function formatPreviewMoney(value: string | number | null | undefined): string {
  if (value == null || value === "") return "importe restringido";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" })
    : "importe restringido";
}

function getInstallationError(e: unknown): string {
  const msg = (e as { message?: string }).message ?? "";
  const translated = getContractErrorMessage(e);
  return translated !== "No se pudo completar la operación. Revisa los datos e inténtalo de nuevo."
    ? translated
    : INSTALLATION_ERROR_MAP[msg] ?? "No se pudo procesar la solicitud. Revisa los campos e inténtalo de nuevo.";
}

interface Props {
  contractId: number;
  customerPartnerId: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
  createdContractLabel?: string;
}

interface PlanningSegmentUI {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
}

export function AddInstallationDialog({ contractId, customerPartnerId, open, onOpenChange, onCreated, createdContractLabel }: Props) {
  const linkPreviewRef = useRef<HTMLDivElement>(null);
  const createFlight = useRef(false);
  const [actionTab, setActionTab] = useState<"create" | "link">("create");
  const [linkSubTab, setLinkSubTab] = useState<"canonical" | "unmapped">("canonical");

  // Step state for creation (Progressive Disclosure)
  const [createStep, setCreateStep] = useState<1 | 2 | 3>(1);

  // Step 1: Main info & Operational Contact
  const [name, setName] = useState("");
  const [siteMode, setSiteMode] = useState<"existing" | "new">("existing");
  const [siteId, setSiteId] = useState("");
  const [siteName, setSiteName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [workOrderType, setWorkOrderType] = useState("installation");
  const [installationType, setInstallationType] = useState("solar");
  const [installedPowerKwp, setInstalledPowerKwp] = useState("");
  const [workDescription, setWorkDescription] = useState("");

  const [contactId, setContactId] = useState(GENERAL_CONTACT_VALUE);
  const [contactAssignmentId, setContactAssignmentId] = useState<number | null>(null);
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [isEmailCustomized, setIsEmailCustomized] = useState(false);
  const [isPhoneCustomized, setIsPhoneCustomized] = useState(false);

  // Step 2: Operations, Team & Planning
  const [maintenanceMode, setMaintenanceMode] = useState<"inherit" | "custom_true" | "custom_false">("inherit");
  const [maintenancePaymentStatus, setMaintenancePaymentStatus] = useState<"account_current" | "payment_pending" | "payment_overdue" | "unknown" | "">("");
  const [isBillableBreakdown, setIsBillableBreakdown] = useState(false);
  const [projectManagerUserId, setProjectManagerUserId] = useState("");
  const [assignedTechnicianIds, setAssignedTechnicianIds] = useState<string[]>([]);

  // Planning Segments
  const [planningSegments, setPlanningSegments] = useState<PlanningSegmentUI[]>([]);
  const [segTitle, setSegTitle] = useState("");
  const [segDescription, setSegDescription] = useState("");
  const [segStartDate, setSegStartDate] = useState("");
  const [segEndDate, setSegEndDate] = useState("");

  // Step 3: Budget & Internal Costs
  const [budget, setBudget] = useState("");
  const [laborPrice, setLaborPrice] = useState("");

  // Post-create result
  const [createdResult, setCreatedResult] = useState<{ installationId: number; installationCode: string } | null>(null);

  // Link state
  const [searchTerm, setSearchTerm] = useState("");
  const [legacySearchTerm, setLegacySearchTerm] = useState("");
  const [debouncedLegacySearchTerm, setDebouncedLegacySearchTerm] = useState("");
  const [selectedInstallationId, setSelectedInstallationId] = useState<number | null>(null);
  const [confirmCustomerResolution, setConfirmCustomerResolution] = useState(false);
  const [confirmEconomicReconciliation, setConfirmEconomicReconciliation] = useState(false);
  const [linkSiteMode, setLinkSiteMode] = useState<"existing" | "new">("existing");
  const [linkSiteId, setLinkSiteId] = useState("");
  const [linkSiteName, setLinkSiteName] = useState("");
  const [linkSiteAddress, setLinkSiteAddress] = useState("");
  const [linkSiteCity, setLinkSiteCity] = useState("");
  const [linkSitePostalCode, setLinkSitePostalCode] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);

  // Queries
  const sitesQuery = trpc.customers.getSites.useQuery({ partnerId: customerPartnerId }, { enabled: open && (actionTab === "create" || actionTab === "link") });
  const sites = (sitesQuery.data ?? []) as Array<{ id: number; name: string; addressLine1: string | null }>;

  const pmQuery = trpc.customers.contracts.listAssignableProjectManagers.useQuery(undefined, { enabled: open && actionTab === "create" });
  const projectManagers = (pmQuery.data ?? []) as Array<{ id: number; name: string; email: string; role: string }>;

  const contractQuery = trpc.customers.contracts.get.useQuery({ contractId }, { enabled: open && actionTab === "create" });
  const contractDetail = contractQuery.data as { contractCode?: string | null; contractualEndDate?: string | null } | undefined;

  const customer360Query = trpc.customers.get360.useQuery({ partnerId: customerPartnerId }, { enabled: open && actionTab === "create" });
  const customer360Data = customer360Query.data;
  const customerContactAssignments = (customer360Data?.contacts ?? []) as InstallationContactChoice[];
  const availableContacts = useMemo(() => dedupeInstallationContactChoices(customerContactAssignments, {
    contractId: Number(contractId),
    siteId: siteMode === "existing" && siteId ? Number(siteId) : null,
  }), [customerContactAssignments, contractId, siteMode, siteId]);
  const techniciansQuery = trpc.users.listTechnicians.useQuery(undefined, { enabled: open && actionTab === "create" });
  const technicians = (techniciansQuery.data ?? []) as Array<{ id: number; name: string; email: string }>;

  // Canonical candidates list
  const linkableQuery = trpc.customers.contracts.listLinkableInstallations.useQuery({ contractId }, { enabled: open && actionTab === "link" && linkSubTab === "canonical" });
  const linkableList = (linkableQuery.data ?? []) as Array<{
    id: number;
    installationCode: string | null;
    name: string | null;
    workDescription: string | null;
    clientId: string;
    status: string;
    address: string;
    siteId: number | null;
    siteName: string | null;
    contractId: number | null;
    contractStatus: string | null;
    legacyEconomicBlock: boolean;
  }>;

  // Unmapped legacy search
  const legacySearchQuery = trpc.customers.contracts.searchLegacyInstallations.useQuery(
    { query: debouncedLegacySearchTerm },
    { enabled: open && actionTab === "link" && linkSubTab === "unmapped" && debouncedLegacySearchTerm.trim().length >= 2 }
  );
  const legacyList = (legacySearchQuery.data ?? []) as Array<{
    id: number;
    installationCode: string | null;
    name: string | null;
    workDescription: string | null;
    clientId: string;
    clientName: string | null;
    status: string;
    address: string;
    siteId: number | null;
    siteName: string | null;
  }>;

  // Server-authoritative preview for selected installation
  const previewSiteInput = linkSiteMode === "existing" && linkSiteId
    ? { siteId: Number(linkSiteId) }
    : linkSiteMode === "new" && linkSiteName.trim() && linkSiteAddress.trim()
      ? { newSite: { name: linkSiteName.trim(), addressLine1: linkSiteAddress.trim(), city: linkSiteCity.trim() || null, postalCode: linkSitePostalCode.trim() || null } }
      : {};
  const previewQuery = trpc.customers.contracts.previewLink.useQuery(
    { contractId, installationId: selectedInstallationId!, ...previewSiteInput },
    { enabled: open && actionTab === "link" && selectedInstallationId != null && selectedInstallationId > 0 }
  );
  const previewData = previewQuery.data;

  // WRITE_ECONOMIC capability gate for the legacy economic correction CTA.
  // Optional-chained so partial test mocks of the contracts namespace remain
  // renderable; the server re-checks the capability authoritatively anyway.
  const capsQuery = trpc.customers.contracts.getMyCapabilities?.useQuery?.(undefined, { enabled: open });
  const canWriteEconomic = Boolean((capsQuery?.data as Record<string, unknown> | undefined)?.canWriteEconomic);

  const create = trpc.customers.contracts.createInstallationForContract.useMutation();
  const reconcileLink = trpc.customers.contracts.reconcileAndLinkInstallation.useMutation();

  function reset() {
    setCreateStep(1);
    setInstalledPowerKwp("");
    setName(""); setSiteId(""); setSiteName(""); setAddressLine1(""); setCity(""); setPostalCode("");
    setWorkOrderType("installation"); setInstallationType("solar"); setWorkDescription("");
    setContactId(GENERAL_CONTACT_VALUE); setContactAssignmentId(null); setClientEmail(""); setClientPhone(""); setIsEmailCustomized(false); setIsPhoneCustomized(false);
    setMaintenanceMode("inherit"); setMaintenancePaymentStatus(""); setIsBillableBreakdown(false); setProjectManagerUserId("");
    setAssignedTechnicianIds([]);
    setPlanningSegments([]); setSegTitle(""); setSegDescription(""); setSegStartDate(""); setSegEndDate("");
    setBudget(""); setLaborPrice("");
    setCreatedResult(null);

    setSearchTerm(""); setLegacySearchTerm(""); setDebouncedLegacySearchTerm(""); setSelectedInstallationId(null); setActionTab("create");
    setLinkSubTab("canonical"); setConfirmCustomerResolution(false); setConfirmEconomicReconciliation(false);
    setLinkSiteMode("existing"); setLinkSiteId(""); setLinkSiteName(""); setLinkSiteAddress(""); setLinkSiteCity(""); setLinkSitePostalCode("");
    setCorrectionOpen(false);
    setError(null);
  }

  useEffect(() => { if (open) { setError(null); reset(); } }, [open]);

  useEffect(() => {
    setConfirmCustomerResolution(false);
    setConfirmEconomicReconciliation(false);
    setLinkSiteMode("existing");
    setLinkSiteId("");
    setLinkSiteName("");
    setLinkSiteAddress("");
    setLinkSiteCity("");
    setLinkSitePostalCode("");
    setCorrectionOpen(false);
    setError(null);
  }, [selectedInstallationId]);

  useEffect(() => {
    const resolution = previewData?.siteResolution;
    if (!resolution?.required || resolution.currentSiteId != null) return;
    if (!linkSiteAddress.trim() && resolution.legacyAddress) setLinkSiteAddress(resolution.legacyAddress);
  }, [previewData?.siteResolution?.required, previewData?.siteResolution?.legacyAddress]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedLegacySearchTerm(legacySearchTerm), 250);
    return () => window.clearTimeout(timer);
  }, [legacySearchTerm]);

  useEffect(() => {
    if (contactId !== GENERAL_CONTACT_VALUE) return;
    if (!isEmailCustomized) setClientEmail(customer360Data?.generalEmail || "");
    if (!isPhoneCustomized) setClientPhone(customer360Data?.generalPhone || "");
  }, [contactId, customer360Data?.generalEmail, customer360Data?.generalPhone, isEmailCustomized, isPhoneCustomized]);

  useEffect(() => {
    if (contactId === GENERAL_CONTACT_VALUE) {
      setContactAssignmentId(null);
      return;
    }
    const selectedContact = availableContacts.find((contact) => contact.id === Number(contactId));
    if (!selectedContact) {
      setContactId(GENERAL_CONTACT_VALUE);
      setContactAssignmentId(null);
      if (!isEmailCustomized) setClientEmail(customer360Data?.generalEmail || "");
      if (!isPhoneCustomized) setClientPhone(customer360Data?.generalPhone || "");
      return;
    }
    setContactAssignmentId(selectedContact.assignmentId);
  }, [availableContacts, contactId, customer360Data?.generalEmail, customer360Data?.generalPhone, isEmailCustomized, isPhoneCustomized]);

  // Handle contact auto-fill
  function handleContactChange(selectedId: string) {
    setContactId(selectedId);
    if (selectedId === GENERAL_CONTACT_VALUE) {
      setContactAssignmentId(null);
      if (!isEmailCustomized) setClientEmail(customer360Data?.generalEmail || "");
      if (!isPhoneCustomized) setClientPhone(customer360Data?.generalPhone || "");
      return;
    }
    const cId = Number(selectedId);
    const selectedContact = availableContacts.find((contact) => contact.id === cId);
    if (!selectedContact) {
      setContactId(GENERAL_CONTACT_VALUE);
      setContactAssignmentId(null);
      if (!isEmailCustomized) setClientEmail(customer360Data?.generalEmail || "");
      if (!isPhoneCustomized) setClientPhone(customer360Data?.generalPhone || "");
      return;
    }
    setContactAssignmentId(selectedContact.assignmentId);
    if (!isEmailCustomized) setClientEmail(selectedContact.primaryEmail || customer360Data?.generalEmail || "");
    if (!isPhoneCustomized) setClientPhone(selectedContact.primaryPhone || customer360Data?.generalPhone || "");
  }

  // Handle adding planning phase
  function handleAddSegment() {
    if (!segTitle.trim()) {
      setError("El título de la fase de planificación es obligatorio.");
      return;
    }
    if (!segStartDate || !segEndDate) {
      setError("Indica las fechas de inicio y fin de la fase.");
      return;
    }
    if (new Date(segEndDate).getTime() < new Date(segStartDate).getTime()) {
      setError("La fecha de fin debe ser posterior o igual a la fecha de inicio.");
      return;
    }
    setError(null);
    setPlanningSegments((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        title: segTitle.trim(),
        description: segDescription.trim(),
        startDate: segStartDate,
        endDate: segEndDate,
      },
    ]);
    setSegTitle("");
    setSegDescription("");
    setSegStartDate("");
    setSegEndDate("");
  }

  function handleRemoveSegment(id: string) {
    setPlanningSegments((prev) => prev.filter((s) => s.id !== id));
  }

  function commitDraftPlanningSegment(): boolean {
    const hasDraft = Boolean(segTitle.trim() || segDescription.trim() || segStartDate || segEndDate);
    if (!hasDraft) return true;
    if (!segTitle.trim() || !segStartDate || !segEndDate) {
      setError("Completa el título y las fechas de la fase, o borra los campos antes de continuar.");
      return false;
    }
    if (segEndDate < segStartDate) {
      setError("La fecha de fin debe ser posterior o igual a la fecha de inicio.");
      return false;
    }
    setPlanningSegments((prev) => [...prev, {
      id: String(Date.now()), title: segTitle.trim(), description: segDescription.trim(),
      startDate: segStartDate, endDate: segEndDate,
    }]);
    setSegTitle(""); setSegDescription(""); setSegStartDate(""); setSegEndDate("");
    return true;
  }

  function validateStep(step: 1 | 2): boolean {
    setError(null);
    if (step === 2 && !commitDraftPlanningSegment()) return false;
    if (step === 1) {
      try { parseInstalledPowerInput(installedPowerKwp); } catch { setError(INSTALLED_POWER_ERROR); return false; }
      if (!name.trim()) { setError("El nombre de la instalación es obligatorio."); return false; }
      if (name.trim().length > INSTALLATION_SHORT_NAME_MAX_LENGTH) { setError("Usa una denominación corta (máximo 80 caracteres) y lleva el detalle a Descripción técnica."); return false; }
      if (siteMode === "existing" && !siteId) { setError("Selecciona una sede para la instalación."); return false; }
      if (siteMode === "new") {
        if (!siteName.trim()) { setError("El nombre de la sede es obligatorio."); return false; }
        if (!addressLine1.trim()) { setError("La dirección de la sede es obligatoria."); return false; }
      }
      return true;
    }
    if (step === 2) {
      return true;
    }
    return true;
  }

  async function handleCreateSubmit() {
    if (createFlight.current || createdResult || !validateStep(1)) return;
    const selectedContactChoice = contactId === GENERAL_CONTACT_VALUE
      ? null
      : availableContacts.find((contact) => contact.id === Number(contactId)) ?? null;
    if (contactId !== GENERAL_CONTACT_VALUE && (!selectedContactChoice || selectedContactChoice.assignmentId !== contactAssignmentId)) {
      setError("El contacto seleccionado ya no pertenece al cliente, contrato o sede elegidos.");
      return;
    }
    createFlight.current = true;
    setSubmitting(true);
    setError(null);
    const maintenancePayload = maintenanceMode === "inherit"
      ? {}
      : maintenanceMode === "custom_false"
      ? { includesMaintenance: false, maintenancePaymentStatus: null }
      : maintenancePaymentStatus === ""
      ? { includesMaintenance: true }
      : { includesMaintenance: true, maintenancePaymentStatus };

    const payload = {
      contractId,
      name: name.trim(),
      workOrderType: workOrderType as "installation" | "breakdown" | "maintenance",
      installationType: installationType || "solar",
      workDescription: workDescription.trim() || null,
      installedPowerKwp: parseInstalledPowerInput(installedPowerKwp),
      projectManagerUserId: projectManagerUserId ? Number(projectManagerUserId) : null,
      ...(siteMode === "existing"
        ? { siteId: Number(siteId) }
        : { newSite: { name: siteName.trim(), addressLine1: addressLine1.trim(), city: city.trim() || null, postalCode: postalCode.trim() || null } }),
      contactId: selectedContactChoice?.id ?? null,
      contactAssignmentId: selectedContactChoice?.assignmentId ?? null,
      clientEmail: clientEmail.trim() || null,
      clientPhone: clientPhone.trim() || null,
      assignedTechnicianIds: assignedTechnicianIds.map((id) => Number(id)).filter((id) => !isNaN(id)),
      budget: budget.trim() || null,
      laborPrice: laborPrice.trim() || null,
      ...maintenancePayload,
      isBillableBreakdown: workOrderType === "breakdown" ? isBillableBreakdown : false,
      planningSegments: planningSegments.map((s) => ({
        title: s.title,
        description: s.description || null,
        startDate: s.startDate,
        endDate: s.endDate,
      })),
    };
    try {
      const res = await create.mutateAsync(payload);
      toast.success(`Instalación ${res.installationCode} creada con éxito`);
      setCreatedResult({ installationId: res.installationId, installationCode: res.installationCode });
      onCreated();
    } catch (e) {
      setError(getInstallationError(e));
    } finally {
      createFlight.current = false;
      setSubmitting(false);
    }
  }

  async function handleLinkSubmit() {
    if (!selectedInstallationId) { setError("Selecciona una instalación existente para vincular."); return; }

    if (
      previewQuery.isLoading ||
      previewQuery.isError ||
      !previewData ||
      previewData.installation?.id !== selectedInstallationId
    ) {
      setError("La previsualización del servidor aún no está disponible; no se puede vincular.");
      return;
    }

    if (previewData.classification === "NEEDS_CUSTOMER_RESOLUTION" && !confirmCustomerResolution) {
      setError("Debes confirmar la asignación del cliente a la instalación legacy.");
      return;
    }
    if (previewData.siteResolution?.required) {
      setError("Debes resolver la sede operativa antes de vincular esta instalación.");
      return;
    }
    if (previewData.classification === "NEEDS_ECONOMIC_RECONCILIATION") {
      if (previewData.resolutionRequired && !confirmCustomerResolution) {
        setError("Debes confirmar la asignación del cliente a la instalación legacy.");
        return;
      }
      if (!confirmEconomicReconciliation) {
        setError("Debes confirmar la reconciliación económica en el contrato.");
        return;
      }
    }
    if (["CONFLICT_OTHER_CUSTOMER", "CONFLICT_OTHER_CONTRACT", "DATA_INTEGRITY_BLOCKED",
         "BLOCKED_STRATEGY_CONFLICT", "BLOCKED_CEILING_CONFLICT", "BLOCKED_IDENTITY_CONFLICT"].includes(previewData.classification)) {
      setError("No se puede vincular esta instalación debido a un conflicto o bloqueo de integridad. El bloqueo no puede ser omitido.");
      return;
    }
    if (previewData.classification === "NEEDS_SITE_RESOLUTION") {
      setError("Esta instalación no tiene sede asignada. Debes resolver la sede antes de vincularla.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await reconcileLink.mutateAsync({
        contractId,
        installationId: selectedInstallationId,
        confirmCustomerResolution,
        confirmEconomicReconciliation,
        ...(linkSiteMode === "existing" && linkSiteId ? { siteId: Number(linkSiteId) } : {}),
        ...(linkSiteMode === "new" ? {
          newSite: {
            name: linkSiteName.trim(),
            addressLine1: linkSiteAddress.trim(),
            city: linkSiteCity.trim() || null,
            postalCode: linkSitePostalCode.trim() || null,
          },
        } : {}),
      });
      toast.success("Instalación vinculada al contrato con éxito");
      onCreated();
      reset();
      onOpenChange(false);
    } catch (e) {
      setError(getInstallationError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const filteredLinkableList = linkableList.filter((inst) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (inst.installationCode && inst.installationCode.toLowerCase().includes(term)) ||
      (inst.name && inst.name.toLowerCase().includes(term)) ||
      (inst.workDescription && inst.workDescription.toLowerCase().includes(term)) ||
      (inst.clientId && inst.clientId.toLowerCase().includes(term)) ||
      (inst.address && inst.address.toLowerCase().includes(term)) ||
      (inst.siteName && inst.siteName.toLowerCase().includes(term))
    );
  });

  const previewUnavailable = selectedInstallationId != null && (
    previewQuery.isLoading ||
    previewQuery.isError ||
    !previewData ||
    previewData.installation?.id !== selectedInstallationId
  );

  return (
    <>
      <Dialog open={open} onOpenChange={value => { if (!createFlight.current) onOpenChange(value); }}>
      <DialogContent className={actionTab === "link"
        ? "flex min-w-0 max-h-[94dvh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-4 sm:max-w-6xl sm:p-6"
        : "flex min-w-0 max-h-[90dvh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-2xl"}>
        <DialogHeader className="shrink-0 pr-6">
          <DialogTitle>Gestión de instalaciones del contrato</DialogTitle>
          <DialogDescription className="break-words">
            {createdContractLabel
              ? `Contrato ${createdContractLabel} ya creado. Si cancelas esta instalación, el contrato se conserva. Puedes añadir otra instalación al terminar esta, sin repetir su economía.`
              : "Crea una nueva instalación vinculada al contrato o asocia una existente con total integridad."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 flex-wrap border-b pb-3 gap-2">
          <Button
            type="button"
            variant={actionTab === "create" ? "default" : "outline"}
            size="sm"
            disabled={submitting}
            onClick={() => { setActionTab("create"); setError(null); }}
          >
            Crear nueva instalación
          </Button>
          <Button
            type="button"
            variant={actionTab === "link" ? "default" : "outline"}
            size="sm"
            disabled={submitting}
            onClick={() => { setActionTab("link"); setError(null); }}
          >
            Vincular existente
          </Button>
        </div>

        <div className="min-h-0 min-w-0 overflow-y-auto overscroll-contain pr-1 [overflow-wrap:anywhere] [&_[data-slot=select-trigger]]:w-full [&_[data-slot=select-trigger]]:min-w-0 [&_[data-slot=select-trigger]]:max-w-full [&_[data-slot=select-trigger][data-size]]:h-auto [&_[data-slot=select-trigger]]:min-h-10 [&_[data-slot=select-trigger]]:whitespace-normal [&_[data-slot=select-trigger]]:text-left [&_[data-slot=select-trigger]_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-trigger]_[data-slot=select-value]]:flex-1 [&_[data-slot=select-trigger]_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-trigger]_[data-slot=select-value]]:block [&_[data-slot=select-trigger]_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-trigger]_[data-slot=select-value]]:[overflow-wrap:anywhere]">
        {actionTab === "create" ? (
          createdResult ? (
            /* Post-creation success view */
            <div className="space-y-4 py-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">¡Instalación creada con éxito!</h3>
                <p className="text-sm text-muted-foreground">
                  Se ha asignado automáticamente el código de expediente:
                </p>
                <Badge variant="outline" className="font-mono text-base px-3 py-1 bg-primary/5 text-primary border-primary/30 mt-2">
                  Expediente #{createdResult.installationCode}
                </Badge>
              </div>
              <div className="pt-4 flex flex-col sm:flex-row justify-center gap-3 [&_button]:min-h-11 [&_button]:h-auto [&_button]:whitespace-normal">
                <Button type="button" variant="outline" onClick={() => reset()}>
                  <Plus className="mr-2 h-4 w-4" /> Añadir otra instalación
                </Button>
                <Button type="button" onClick={() => { reset(); onOpenChange(false); }}>
                  Volver al contrato
                </Button>
              </div>
            </div>
          ) : (
            /* 3-Step Progressive Disclosure Flow */
            <div className="space-y-4">
              {/* Stepper Header */}
              <div className="grid grid-cols-3 gap-2 text-center border-b pb-3 text-xs font-medium">
                <div className={`p-1.5 rounded transition-colors ${createStep === 1 ? "bg-primary text-primary-foreground font-semibold" : "bg-muted text-muted-foreground"}`}>
                  1. Datos y Contacto
                </div>
                <div className={`p-1.5 rounded transition-colors ${createStep === 2 ? "bg-primary text-primary-foreground font-semibold" : "bg-muted text-muted-foreground"}`}>
                  2. Equipo (opcional)
                </div>
                <div className={`p-1.5 rounded transition-colors ${createStep === 3 ? "bg-primary text-primary-foreground font-semibold" : "bg-muted text-muted-foreground"}`}>
                  3. Resumen
                </div>
              </div>

              {/* STEP 1: Main Info & Operational Contact */}
              {createStep === 1 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="ai-name">Denominación de la instalación *</Label>
                    <Input
                      id="ai-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ej. Instalación Fotovoltaica Sede Central"
                      maxLength={INSTALLATION_SHORT_NAME_MAX_LENGTH}
                      aria-describedby="ai-name-help"
                    />
                  </div>

                  <p id="ai-name-help" className="text-xs text-muted-foreground">Nombre corto para identificar la obra (máximo 80 caracteres). El alcance y los detalles van en Descripción técnica.</p>

                  <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label>Sede operativa *</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant={siteMode === "existing" ? "default" : "outline"} size="sm" onClick={() => setSiteMode("existing")}>Existente</Button>
                      <Button type="button" variant={siteMode === "new" ? "default" : "outline"} size="sm" onClick={() => setSiteMode("new")}>Nueva</Button>
                    </div>
                  </div>

                  {siteMode === "existing" && (
                    <div className="space-y-1.5">
                      <Label>Sede del cliente *</Label>
                      <Select value={siteId} onValueChange={setSiteId}>
                        <SelectTrigger><SelectValue placeholder="Selecciona una sede del cliente" /></SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                          {sites.map((s) => (
                            <SelectItem key={s.id} value={String(s.id)}>
                              {s.name}{s.addressLine1 ? ` — ${s.addressLine1}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {siteMode === "new" && (
                    <div className="space-y-3 p-3 border rounded-md bg-muted/20">
                      <div className="space-y-1.5">
                        <Label htmlFor="ai-site-name">Nombre de la sede *</Label>
                        <Input id="ai-site-name" value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="Ej. Nave Principal" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="ai-address">Dirección *</Label>
                        <Input id="ai-address" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} placeholder="Calle / Avda. y número" />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="ai-city">Municipio</Label>
                          <Input id="ai-city" value={city} onChange={(e) => setCity(e.target.value)} />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="ai-postal">Código postal</Label>
                          <Input id="ai-postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Tipo de trabajo</Label>
                      <Select value={workOrderType} onValueChange={setWorkOrderType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                          {Object.entries(WORK_ORDER_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ai-type">Tipo de instalación</Label>
                      <Input id="ai-type" value={installationType} onChange={(e) => setInstallationType(e.target.value)} />
                    </div>
                  </div>

                  {workOrderType === "breakdown" && (
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id="ai-is-billable-breakdown"
                        checked={isBillableBreakdown}
                        onCheckedChange={(c) => setIsBillableBreakdown(Boolean(c))}
                      />
                      <Label htmlFor="ai-is-billable-breakdown" className="font-medium cursor-pointer">
                        Avería facturable (isBillableBreakdown)
                      </Label>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label htmlFor="ai-desc">Descripción técnica</Label>
                    <Textarea id="ai-desc" value={workDescription} onChange={(e) => setWorkDescription(e.target.value)} placeholder="Detalle técnico u observaciones del proyecto" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ai-installed-power">Potencia instalada DC (kWp)</Label>
                    <Input id="ai-installed-power" inputMode="decimal" value={installedPowerKwp} onChange={e => setInstalledPowerKwp(e.target.value)} aria-describedby="ai-installed-power-help" placeholder="Ej. 5,5" />
                    <p id="ai-installed-power-help" className="text-xs text-muted-foreground">Opcional. Potencia realmente instalada, hasta 3 decimales. Déjala vacía si se desconoce.</p>
                  </div>

                  <div className="pt-2 border-t space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5" /> Contacto operativo y notificaciones
                    </h4>

                    <div className="space-y-1.5">
                      <Label>Contacto de Cliente 360 (opcional)</Label>
                      <Select value={contactId} onValueChange={handleContactChange}>
                        <SelectTrigger><SelectValue placeholder="Selecciona un contacto del cliente" /></SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                          <SelectItem value={GENERAL_CONTACT_VALUE}>Sin contacto específico (usar general del cliente)</SelectItem>
                          {availableContacts.map((c) => {
                            const isSiteContact = siteId && c.scope === "site" && c.siteId === Number(siteId);
                            return (
                              <SelectItem key={c.assignmentId} value={String(c.id)}>
                                {c.displayName} {c.role ? `(${c.role})` : ""} {isSiteContact ? "★ Sede" : ""}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="ai-email">Email de notificaciones</Label>
                        <Input
                          id="ai-email"
                          type="email"
                          value={clientEmail}
                          onChange={(e) => { setClientEmail(e.target.value); setIsEmailCustomized(true); }}
                          placeholder="notificaciones@cliente.com"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="ai-phone">Teléfono de notificaciones</Label>
                        <Input
                          id="ai-phone"
                          value={clientPhone}
                          onChange={(e) => { setClientPhone(e.target.value); setIsPhoneCustomized(true); }}
                          placeholder="+34 600 000 000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Maintenance, PM, Technicians & Planning Segments */}
              {createStep === 2 && (
                <div className="space-y-4">
                  <div className="p-3 border rounded-md bg-muted/20 space-y-3">
                    <div className="space-y-1.5">
                      <Label>Contrato de mantenimiento</Label>
                      <Select
                        value={maintenanceMode}
                        onValueChange={(val) => {
                          setMaintenanceMode(val as "inherit" | "custom_true" | "custom_false");
                          if (val !== "custom_true") setMaintenancePaymentStatus("");
                        }}
                      >
                        <SelectTrigger id="ai-maint-mode">
                          <SelectValue placeholder="Heredar del contrato" />
                        </SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                          <SelectItem value="inherit">Heredar del contrato (por defecto)</SelectItem>
                          <SelectItem value="custom_true">Sí (Incluye mantenimiento)</SelectItem>
                          <SelectItem value="custom_false">No (Sin mantenimiento)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {maintenanceMode === "custom_true" && (
                      <div className="space-y-1.5 pl-2">
                        <Label htmlFor="ai-maint-status">Estado del pago de mantenimiento</Label>
                        <Select
                          value={maintenancePaymentStatus}
                          onValueChange={(val) => setMaintenancePaymentStatus(val as any)}
                        >
                          <SelectTrigger id="ai-maint-status">
                            <SelectValue placeholder="Heredar del contrato o seleccionar" />
                          </SelectTrigger>
                          <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                            <SelectItem value="account_current">Al corriente</SelectItem>
                            <SelectItem value="payment_pending">Pago pendiente</SelectItem>
                            <SelectItem value="payment_overdue">Vencido</SelectItem>
                            <SelectItem value="unknown">Desconocido</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Project Manager responsable</Label>
                      <Select value={projectManagerUserId} onValueChange={setProjectManagerUserId}>
                        <SelectTrigger><SelectValue placeholder="Heredar PM del contrato" /></SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                          {projectManagers.map((u) => (
                            <SelectItem key={u.id} value={String(u.id)}>
                              {u.name}{u.email ? ` · ${u.email}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5" data-testid="contract-deadline-readonly">
                      <Label>Compromiso contractual</Label>
                      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm" aria-readonly="true">
                        <div>Contrato {contractDetail?.contractCode || createdContractLabel || `CTR-${contractId}`}</div>
                        <div className="text-muted-foreground">Fecha límite: {contractDetail?.contractualEndDate ? new Date(`${contractDetail.contractualEndDate}T00:00:00`).toLocaleDateString("es-ES") : "—"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Technicians assignment */}
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5">
                      <Wrench className="h-3.5 w-3.5" /> Asignación inicial de técnicos (opcional)
                    </Label>
                    <div className="max-h-28 overflow-y-auto border rounded-md p-2 space-y-1.5 bg-background">
                      {technicians.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No hay técnicos disponibles para asignar.</p>
                      ) : (
                        technicians.map((tech) => {
                          const isChecked = assignedTechnicianIds.includes(String(tech.id));
                          return (
                            <div key={tech.id} className="flex items-center gap-2 text-xs">
                              <Checkbox
                                id={`tech-${tech.id}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setAssignedTechnicianIds((prev) => [...prev, String(tech.id)]);
                                  } else {
                                    setAssignedTechnicianIds((prev) => prev.filter((id) => id !== String(tech.id)));
                                  }
                                }}
                              />
                              <label htmlFor={`tech-${tech.id}`} className="cursor-pointer">
                                {tech.name} <span className="text-muted-foreground">({tech.email})</span>
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Planning Segments */}
                  <div className="pt-2 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" /> Fases de planificación V1
                      </h4>
                      <span className="text-xs text-muted-foreground">
                        {planningSegments.length} {planningSegments.length === 1 ? "fase creada" : "fases creadas"}
                      </span>
                    </div>

                    {/* Add Phase Form */}
                    <div className="p-3 border rounded-md bg-muted/20 space-y-2 text-xs">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div className="space-y-1 sm:col-span-2">
                          <Label htmlFor="seg-title">Denominación de la fase *</Label>
                          <Input id="seg-title" value={segTitle} onChange={(e) => setSegTitle(e.target.value)} placeholder="Ej. Obra civil e infraestructura" />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="seg-start">Fecha inicio *</Label>
                          <Input id="seg-start" type="date" value={segStartDate} onChange={(e) => setSegStartDate(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="seg-end">Fecha fin *</Label>
                          <Input id="seg-end" type="date" value={segEndDate} onChange={(e) => setSegEndDate(e.target.value)} />
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                          <Label htmlFor="seg-desc">Descripción (opcional)</Label>
                          <Input id="seg-desc" value={segDescription} onChange={(e) => setSegDescription(e.target.value)} placeholder="Detalle de tareas previas" />
                        </div>
                      </div>
                      <div className="flex justify-end pt-1">
                        <Button type="button" size="sm" variant="secondary" onClick={handleAddSegment}>
                          <Plus className="mr-1 h-3.5 w-3.5" /> Añadir fase
                        </Button>
                      </div>
                    </div>

                    {/* Planning segments list */}
                    {planningSegments.length > 0 && (
                      <div className="space-y-1.5 max-h-36 overflow-y-auto">
                        {planningSegments.map((seg) => (
                          <div key={seg.id} className="flex items-center justify-between p-2 rounded border bg-background text-xs">
                            <div className="space-y-0.5">
                              <span className="font-medium text-foreground">{seg.title}</span>
                              <div className="text-muted-foreground flex gap-2">
                                <span>{seg.startDate} al {seg.endDate}</span>
                                {seg.description && <span>· {seg.description}</span>}
                              </div>
                            </div>
                            <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => handleRemoveSegment(seg.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 3: Budget, Labor & Economic Summary */}
              {createStep === 3 && (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="ai-budget">Referencia de presupuesto interno</Label>
                      <Input id="ai-budget" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Ej. PRES-2026-089" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ai-labor">Coste interno mano de obra (€)</Label>
                      <Input id="ai-labor" type="number" step="0.01" value={laborPrice} onChange={(e) => setLaborPrice(e.target.value)} placeholder="0.00" />
                    </div>
                  </div>

                  <Alert className="border-blue-200 bg-blue-50/50 text-blue-900">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertTitle className="text-blue-900 font-semibold">Fuente de verdad económica única en el Contrato</AlertTitle>
                    <AlertDescription className="mt-1 text-xs text-blue-800 space-y-1.5">
                      <p>
                        Los importes de licitación, hitos de facturación, certificaciones y esquemas de cobro no se configuran por instalación individual. La gestión económica del proyecto es centralizada a nivel del contrato.
                      </p>
                      <p className="font-medium text-blue-950">
                        Una vez creada la instalación, consulta o edita la planificación económica en la pestaña "Economía" del contrato.
                      </p>
                    </AlertDescription>
                  </Alert>

                  {/* Summary recap */}
                  <div className="rounded-md border p-3 bg-muted/20 space-y-2 text-xs">
                    <span className="font-semibold text-foreground">Resumen de la instalación a crear:</span>
                    <ul className="space-y-1 text-muted-foreground list-disc pl-4">
                      <li><strong>Nombre:</strong> {name || "Sin especificar"}</li>
                      <li><strong>Sede:</strong> {siteMode === "existing" ? (sites.find((s) => String(s.id) === siteId)?.name || "Existente") : (siteName || "Nueva")}</li>
                      <li><strong>Trabajo / Tipo:</strong> {WORK_ORDER_LABELS[workOrderType] ?? "Otro"} · {installationType}</li>
                      <li><strong>Contacto / Email:</strong> {clientEmail || "General del cliente"}</li>
                      <li>
                        <strong>Mantenimiento:</strong>{" "}
                        {maintenanceMode === "inherit"
                          ? "Heredado del contrato"
                          : maintenanceMode === "custom_true"
                          ? `Sí (${
                              maintenancePaymentStatus === "account_current"
                                ? "Al corriente"
                                : maintenancePaymentStatus === "payment_pending"
                                ? "Pago pendiente"
                                : maintenancePaymentStatus === "payment_overdue"
                                ? "Vencido"
                                : maintenancePaymentStatus === "unknown"
                                ? "Desconocido"
                                : "Heredado del contrato"
                            })`
                          : "No"}
                      </li>
                      {workOrderType === "breakdown" && (
                        <li><strong>Avería facturable:</strong> {isBillableBreakdown ? "Sí" : "No"}</li>
                      )}
                      <li><strong>Fases de planificación:</strong> {planningSegments.length} {planningSegments.length === 1 ? "fase" : "fases"}</li>
                      <li><strong>Técnicos asignados:</strong> {assignedTechnicianIds.length}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          /* LINK EXISTING FLOW (Intact & untouched logic) */
          <div className="space-y-4">
            <div className="flex flex-wrap border-b pb-2 gap-2 text-sm">
              <button
                type="button"
                className={`pb-1 px-2 border-b-2 font-medium transition-colors ${
                  linkSubTab === "canonical" ? "border-ods-navigation text-ods-navigation" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => { setLinkSubTab("canonical"); setSelectedInstallationId(null); setError(null); }}
              >
                Instalaciones del cliente
              </button>
              <button
                type="button"
                className={`pb-1 px-2 border-b-2 font-medium transition-colors ${
                  linkSubTab === "unmapped" ? "border-ods-navigation text-ods-navigation" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => { setLinkSubTab("unmapped"); setSelectedInstallationId(null); setError(null); }}
              >
                Búsqueda legacy sin cliente
              </button>
            </div>

            {linkSubTab === "canonical" ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="link-search">Buscar instalación existente del cliente</Label>
                  <Input
                    id="link-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Filtrar por código, denominación, ref. cliente o dirección..."
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {linkableQuery.isLoading ? (
                    <p role="status" className="text-sm text-muted-foreground p-2 sm:col-span-2">Cargando instalaciones vinculables...</p>
                  ) : linkableQuery.isError ? (
                    <p role="alert" className="text-sm text-destructive p-2">No se pudieron cargar las instalaciones. Vuelve a intentarlo.</p>
                  ) : filteredLinkableList.length === 0 ? (
                    <p role="status" className="text-sm text-muted-foreground p-2 sm:col-span-2">No se encontraron instalaciones desvinculadas para este cliente.</p>
                  ) : (
                    filteredLinkableList.map((inst) => {
                      const isSelected = selectedInstallationId === inst.id;
                      const hasBlock = inst.legacyEconomicBlock;
                      return (
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          key={inst.id}
                          onClick={() => setSelectedInstallationId(inst.id)}
                          className={`min-w-0 w-full p-4 rounded-lg border text-left transition-colors break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:bg-accent/50"
                          }`}
                        >
                          <span className="flex flex-wrap items-start justify-between gap-2 text-base font-semibold">
                            <span>{resolveInstallationTitle(inst)}</span>
                            <span className="flex flex-wrap items-center gap-2">
                              {hasBlock && (
                                <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
                                  Reconciliación requerida
                                </Badge>
                              )}
                              <span className="font-mono text-xs text-muted-foreground">Exp: {inst.installationCode || "N/A"}</span>
                            </span>
                          </span>
                          <span className="text-sm text-muted-foreground mt-2 flex flex-wrap gap-x-2 gap-y-1 leading-relaxed">
                            <span>Ref: {inst.clientId}</span>
                            <span>·</span>
                            <span>Estado: {INSTALLATION_STATUS_LABEL[inst.status] ?? "Estado registrado"}</span>
                            {inst.siteName && <span>· Sede: {inst.siteName}</span>}
                            {inst.address && <span>· {inst.address}</span>}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="legacy-search">Búsqueda legacy en expedientes no asignados</Label>
                  <Input
                    id="legacy-search"
                    value={legacySearchTerm}
                    onChange={(e) => setLegacySearchTerm(e.target.value)}
                    aria-describedby="legacy-search-help"
                    placeholder="Referencia, NIF, cliente o dirección…"
                  />
                  <p id="legacy-search-help" className="text-sm text-muted-foreground">
                    Busca por referencia, NIF, cliente histórico o dirección. Solo aparecen instalaciones legacy sin cliente asignado.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {legacySearchTerm.trim().length < 2 ? (
                    <p role="status" className="text-sm text-muted-foreground p-2 sm:col-span-2">Introduce al menos 2 caracteres para buscar en instalaciones legacy.</p>
                  ) : legacySearchQuery.isLoading || debouncedLegacySearchTerm !== legacySearchTerm ? (
                    <p role="status" className="text-sm text-muted-foreground p-2 sm:col-span-2">Buscando en catálogo legacy...</p>
                  ) : legacySearchQuery.isError ? (
                    <p role="alert" className="text-sm text-destructive p-2">No se pudo completar la búsqueda legacy. Vuelve a intentarlo.</p>
                  ) : legacyList.length === 0 ? (
                    <p role="status" className="text-sm text-muted-foreground p-2 sm:col-span-2">No se encontraron instalaciones legacy sin cliente asignado.</p>
                  ) : (
                    legacyList.map((inst) => {
                      const isSelected = selectedInstallationId === inst.id;
                      return (
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          key={inst.id}
                          onClick={() => setSelectedInstallationId(inst.id)}
                          className={`min-w-0 w-full p-4 rounded-lg border text-left transition-colors break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:bg-accent/50"
                          }`}
                        >
                          <span className="flex flex-wrap items-start justify-between gap-2 text-base font-semibold">
                            <span>{resolveInstallationTitle(inst)}</span>
                            <Badge variant="outline" className="text-purple-700 border-purple-300 bg-purple-50">
                              Legacy sin cliente
                            </Badge>
                          </span>
                          <span className="text-sm text-muted-foreground mt-2 flex flex-wrap gap-x-2 gap-y-1 leading-relaxed">
                            <span>Exp: {inst.installationCode || inst.clientId}</span>
                            {inst.clientName && <span>· Cliente legacy: {inst.clientName}</span>}
                            <span>· Estado: {INSTALLATION_STATUS_LABEL[inst.status] ?? "Estado registrado"}</span>
                            {inst.siteName && <span>· Sede: {inst.siteName}</span>}
                            {inst.address && <span>· {inst.address}</span>}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {selectedInstallationId != null && (
              <div ref={linkPreviewRef} tabIndex={-1} role="region" aria-label="Previsualización de vinculación" className="border rounded-md p-3 space-y-3 bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">Previsualización de vinculación</span>
                  {previewQuery.isLoading ? (
                    <span className="text-xs text-muted-foreground">Verificando en servidor...</span>
                  ) : previewData ? (
                    <Badge variant={
                      (previewData.classification === "SAFE_TO_LINK" || previewData.classification === "SAFE_LINK") ? "default" :
                      (previewData.classification.startsWith("CONFLICT") || previewData.classification.startsWith("BLOCKED") || previewData.classification === "DATA_INTEGRITY_BLOCKED") ? "destructive" : "secondary"
                    }>
                      {CLASSIFICATION_LABEL[previewData.classification] ?? "Requiere revisión"}
                    </Badge>
                  ) : null}
                </div>

                {previewData && (
                  <div className="space-y-2 text-xs">
                    {(previewData.siteResolution?.required || linkSiteMode === "new") && (
                      <Alert className="py-2 border-amber-300 bg-amber-50 text-amber-900">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Sede operativa requerida</AlertTitle>
                        <AlertDescription className="mt-1 space-y-3 text-xs">
                          <p>
                            Esta instalación no tiene sede. Selecciona una sede del Cliente 360 o crea una nueva con la dirección legacy propuesta.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button type="button" size="sm" variant={linkSiteMode === "existing" ? "default" : "outline"} onClick={() => setLinkSiteMode("existing")}>
                              Sede existente
                            </Button>
                            <Button type="button" size="sm" variant={linkSiteMode === "new" ? "default" : "outline"} onClick={() => setLinkSiteMode("new")}>
                              Crear sede
                            </Button>
                          </div>
                          {linkSiteMode === "existing" ? (
                            <Select value={linkSiteId || "__NO_SITE_SELECTED__"} onValueChange={(value) => setLinkSiteId(value === "__NO_SITE_SELECTED__" ? "" : value)}>
                              <SelectTrigger><SelectValue placeholder="Selecciona una sede" /></SelectTrigger>
                              <SelectContent className="max-w-[calc(100vw-3rem)] whitespace-normal [overflow-wrap:anywhere]">
                                <SelectItem value="__NO_SITE_SELECTED__">Selecciona una sede</SelectItem>
                                {sites.map((site) => (
                                  <SelectItem key={site.id} value={String(site.id)}>
                                    {site.name}{site.addressLine1 ? ` — ${site.addressLine1}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="grid gap-2 sm:grid-cols-2">
                              <div className="space-y-1 sm:col-span-2">
                                <Label htmlFor="legacy-site-name">Nombre de la sede *</Label>
                                <Input id="legacy-site-name" value={linkSiteName} onChange={(event) => setLinkSiteName(event.target.value)} placeholder="Nombre de la sede" />
                              </div>
                              <div className="space-y-1 sm:col-span-2">
                                <Label htmlFor="legacy-site-address">Dirección *</Label>
                                <Input id="legacy-site-address" value={linkSiteAddress} onChange={(event) => setLinkSiteAddress(event.target.value)} />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor="legacy-site-city">Municipio</Label>
                                <Input id="legacy-site-city" value={linkSiteCity} onChange={(event) => setLinkSiteCity(event.target.value)} />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor="legacy-site-postal">Código postal</Label>
                                <Input id="legacy-site-postal" value={linkSitePostalCode} onChange={(event) => setLinkSitePostalCode(event.target.value)} />
                              </div>
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    {previewData.resolutionRequired && (
                      <Alert className="py-2 border-amber-300 bg-amber-50 text-amber-900">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Asociación de cliente requerida</AlertTitle>
                        <AlertDescription className="mt-1 space-y-2 text-xs">
                          <p>Esta instalación todavía no está asociada al nuevo Cliente 360 ({previewData.targetContract?.title}).</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Checkbox
                              id="confirm-customer-res"
                              checked={confirmCustomerResolution}
                              onCheckedChange={(c) => setConfirmCustomerResolution(Boolean(c))}
                            />
                            <label htmlFor="confirm-customer-res" className="font-medium cursor-pointer">
                              Confirmar la asignación explícita de la instalación a este Cliente 360
                            </label>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    {previewData.hasLegacyEconomicSources && (
                      <>
                        <Alert className="py-2 border-amber-300 bg-amber-50 text-amber-900">
                          <Info className="h-4 w-4" />
                          <AlertTitle>Reconciliación económica requerida</AlertTitle>
                          <AlertDescription className="mt-1 space-y-2 text-xs">
                            <p>
                              Esta instalación tiene fuentes económicas legacy (hitos: {previewData.economicSummary?.milestoneCount}, ajustes: {previewData.economicSummary?.adjustmentCount}, certificaciones: {previewData.economicSummary?.certificationCount}, avisos: {previewData.economicSummary?.noticeCount}).
                              La economía pasará a ser gestionada de forma única en el contrato.
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Checkbox
                                id="confirm-economic-rec"
                                checked={confirmEconomicReconciliation}
                                onCheckedChange={(c) => setConfirmEconomicReconciliation(Boolean(c))}
                              />
                              <label htmlFor="confirm-economic-rec" className="font-medium cursor-pointer">
                                Confirmar reconciliación e integración económica en el contrato
                              </label>
                            </div>
                          </AlertDescription>
                        </Alert>

                        <div className="rounded border bg-background p-3 space-y-2">
                          <p className="font-medium">Detalle de la verificación de vinculación</p>
                          {previewData.targetContract?.originalContractAmount != null && (
                            <p>
                              Contrato: {formatPreviewMoney(previewData.targetContract.originalContractAmount)} original · {formatPreviewMoney(previewData.targetContract.effectiveContractAmount)} efectivo
                            </p>
                          )}
                          {previewData.economicSummary?.inlinePrices && (
                            <p>
                              Importes históricos: instalación {formatPreviewMoney(previewData.economicSummary.inlinePrices.installationPrice)} · mano de obra {formatPreviewMoney(previewData.economicSummary.inlinePrices.laborPrice)} · presupuesto {formatPreviewMoney(previewData.economicSummary.inlinePrices.budget)}
                            </p>
                          )}
                          {(previewData.economicSummary?.milestones?.length ?? 0) > 0 && (
                            <div>
                              <p className="font-medium">Hitos</p>
                              <ul className="list-disc pl-5">
                                {previewData.economicSummary.milestones?.map((milestone) => (
                                  <li key={milestone.id}>
                                    {milestone.label} · {ECONOMIC_STATUS_LABEL[milestone.status] ?? "Estado registrado"}
                                    {milestone.lifecycleGroup != null && ` · ciclo ${ECONOMIC_STATUS_LABEL[milestone.lifecycleGroup] ?? "Otro estado"}`}
                                    {milestone.fixedAmount != null || milestone.calculatedAmount != null
                                      ? ` · fijo ${formatPreviewMoney(milestone.fixedAmount)} · calculado ${formatPreviewMoney(milestone.calculatedAmount)}`
                                      : ` · ${formatPreviewMoney(milestone.amount)}`}
                                    {milestone.invoiceNumber != null && ` · factura ${milestone.invoiceNumber}`}
                                    {milestone.invoiceAmount != null && ` · importe factura ${formatPreviewMoney(milestone.invoiceAmount)}`}
                                    {milestone.invoiceDate != null && ` · fecha factura ${milestone.invoiceDate}`}
                                    {milestone.paymentAmount != null && ` · importe cobro ${formatPreviewMoney(milestone.paymentAmount)}`}
                                    {milestone.paymentDate != null && ` · fecha cobro ${milestone.paymentDate}`}
                                    {milestone.accountingNotifiedAt != null && ` · contabilidad ${milestone.accountingNotifiedAt}`}
                                    {milestone.certificationId != null && ` · certificación #${milestone.certificationId}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {previewData.economicProjection?.effectiveAmountCents != null && (
                            <div className="rounded bg-muted/60 p-2 space-y-1">
                              <p className="font-medium">Proyección económica posterior a la vinculación</p>
                              <p>Techo contractual: {formatPreviewMoney(previewData.economicProjection.effectiveAmountCents)} · comprometido: {formatPreviewMoney(previewData.economicProjection.projectedCommittedCents)} · disponible: {formatPreviewMoney(previewData.economicProjection.availableCents)}</p>
                              <p>Techo de hitos: {formatPreviewMoney(previewData.economicProjection.manualMilestoneCeilingCents)} · techo de certificaciones: {formatPreviewMoney(previewData.economicProjection.certificationCeilingCents)}</p>
                              {previewData.economicProjection.differenceCents != null &&
                                Number(previewData.economicProjection.differenceCents) > 0 && (
                                  <p className="font-medium text-destructive">
                                    Diferencia exacta sobre el techo: {formatPreviewMoney(previewData.economicProjection.differenceCents)}
                                  </p>
                                )}
                            </div>
                          )}
                          {(previewData.economicProjection?.conflictStory?.length ?? 0) > 0 && (
                            <div className="rounded border border-amber-300 bg-amber-50/60 p-2 space-y-1 text-amber-900">
                              <p className="font-medium">Historia económica del conflicto</p>
                              <ul className="list-disc pl-5 space-y-0.5">
                                {previewData.economicProjection!.conflictStory!.map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(previewData.economicSummary?.adjustments?.length ?? 0) > 0 && (
                            <div>
                              <p className="font-medium">Ajustes</p>
                              <ul className="list-disc pl-5">
                                {previewData.economicSummary.adjustments?.map((adjustment) => (
                                  <li key={adjustment.id}>{adjustment.title} · {ECONOMIC_STATUS_LABEL[adjustment.status] ?? "Estado registrado"} · {formatPreviewMoney(adjustment.amount)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(previewData.economicSummary?.certifications?.length ?? 0) > 0 && (
                            <div>
                              <p className="font-medium">Certificaciones</p>
                              <ul className="list-disc pl-5">
                                {previewData.economicSummary.certifications?.map((certification) => (
                                  <li key={certification.id}>#{certification.certificationNumber} · {ECONOMIC_STATUS_LABEL[certification.status] ?? "Estado registrado"} · {formatPreviewMoney(certification.amount)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(previewData.economicSummary?.notices?.length ?? 0) > 0 && (
                            <div>
                              <p className="font-medium">Avisos</p>
                              <ul className="list-disc pl-5">
                                {previewData.economicSummary.notices?.map((notice) => (
                                  <li key={notice.id}>{NOTICE_EVENT_LABEL[notice.eventType] ?? "Aviso registrado"} · {ECONOMIC_STATUS_LABEL[notice.status] ?? "Estado registrado"}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {previewData.conflicts.length > 0 && (
                      <div className="p-2 border border-destructive/40 bg-destructive/10 rounded text-destructive space-y-1">
                        <p className="font-medium flex items-center gap-1">
                          <XCircle className="h-3.5 w-3.5" /> Conflictos / Bloqueos:
                        </p>
                        {previewData.conflicts.map((c, i) => (
                          <p key={i}>• {getContractErrorMessage(c)}</p>
                        ))}
                      </div>
                    )}

                    {/* PR2: audited legacy data correction. CTA only when the
                        persisted preview is blocked by the legacy economic
                        ceiling and the user holds WRITE_ECONOMIC. The link
                        gate itself is never overridden by this action. */}
                    {canWriteEconomic &&
                      previewData.classification === "BLOCKED_CEILING_CONFLICT" &&
                      previewData.installation?.id === selectedInstallationId && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setCorrectionOpen(true)}
                        >
                          Revisar / corregir economía legacy
                        </Button>
                      )}
                  </div>
                )}
                {previewUnavailable && !previewQuery.isLoading && (
                  <Alert variant="destructive" className="py-2">
                    <XCircle className="h-4 w-4" />
                    <AlertTitle>Previsualización no disponible</AlertTitle>
                    <AlertDescription className="text-xs">
                      No se puede vincular sin una verificación válida de los datos. Corrige el error o inténtalo de nuevo.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive font-medium">{error}</p>}

        </div>

        <DialogFooter className="shrink-0 flex-col-reverse gap-2 border-t pt-3 sm:flex-row [&_button]:min-h-11 [&_button]:h-auto [&_button]:whitespace-normal">
          <Button type="button" variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>

          {actionTab === "create" && !createdResult && (
            <>
              {createStep === 1 && (
                <Button type="button" variant="secondary" disabled={submitting} onClick={() => { if (validateStep(1)) setCreateStep(3); }}>
                  Ir al resumen
                </Button>
              )}
              {createStep > 1 && (
                <Button type="button" variant="secondary" disabled={submitting} onClick={() => setCreateStep((prev) => (prev - 1) as any)}>
                  Anterior
                </Button>
              )}
              {createStep < 3 ? (
                <Button
                  type="button"
                  onClick={() => {
                    if (validateStep(createStep as 1 | 2)) {
                      setCreateStep((prev) => (prev + 1) as any);
                    }
                  }}
                >
                  Siguiente
                </Button>
              ) : (
                <Button type="button" disabled={submitting} onClick={handleCreateSubmit}>
                  {submitting ? "Añadiendo..." : "Añadir instalación"}
                </Button>
              )}
            </>
          )}

          {actionTab === "link" && selectedInstallationId != null && (
            <Button type="button" variant="secondary" onClick={() => {
              linkPreviewRef.current?.scrollIntoView({ block: "start" });
              linkPreviewRef.current?.focus({ preventScroll: true });
            }}>
              Revisar selección
            </Button>
          )}

          {actionTab === "link" && (
            <Button
              type="button"
              disabled={submitting || !selectedInstallationId || previewUnavailable || Boolean(previewData?.siteResolution?.required) || (previewData != null && [
                "CONFLICT_OTHER_CUSTOMER", "CONFLICT_OTHER_CONTRACT", "DATA_INTEGRITY_BLOCKED",
                "BLOCKED_STRATEGY_CONFLICT", "BLOCKED_CEILING_CONFLICT", "BLOCKED_IDENTITY_CONFLICT",
                "NEEDS_SITE_RESOLUTION",
              ].includes(previewData.classification))}
              onClick={handleLinkSubmit}
            >
              {submitting ? "Vinculando..." : "Vincular instalación"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
      {selectedInstallationId != null && (
        <LegacyEconomicCorrectionDialog
          contractId={contractId}
          installationId={selectedInstallationId}
          currentInstallationPrice={
            previewData?.economicSummary?.inlinePrices?.installationPrice ?? null
          }
          open={correctionOpen}
          onOpenChange={setCorrectionOpen}
          onCorrected={() => {
            // Refetch the persisted preview: the link gate re-evaluates from
            // server state only; a correction never bypasses a still-blocked
            // ceiling.
            void previewQuery.refetch?.();
          }}
        />
      )}
    </>
  );
}
